import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Trash2,
  RefreshCw,
  Search,
  AlertTriangle,
  RotateCcw,
  Filter,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { ApiError } from "@/lib/api/chatApi";
import {
  estadoSincronizacion,
  etiquetaModelo,
  modelosParaTarget,
  origenConocido,
  type Asignacion,
  type ModeloCurado,
  type ModeloDelCatalogo,
  type ModelosApi,
  type PreciosModelo,
} from "@/lib/api/modelosApi";
import GrafoModelos from "./GrafoModelos";
import InsigniasAgente from "./InsigniasAgente";
import {
  contarArrastres,
  filtrarAsignaciones,
  resumirAsignaciones,
  subagentesArrastrados,
  type Coincidencia,
} from "./filtroAsignaciones";
import { useVistaAsignaciones, VISTAS } from "./vistaAsignaciones";
import { usePantallaCompleta } from "./pantallaCompleta";

/**
 * Pestana "Modelos" del panel de administracion.
 *
 * Fichero propio y no una seccion mas de `AdminPage.tsx`: ese fichero ya pasa de
 * 1.100 lineas y esta tarea existe en parte por eso. Todo el estado de la
 * pestana vive AQUI (a diferencia de las otras tres, cuyo estado se quedo en el
 * padre): no hay busqueda ni paginacion que el admin pueda perder al cambiar de
 * pestana, y lo que si hay —las listas— se recarga al montar.
 *
 * Sin librerias de tablas ni de desplegables: a mano, como las pestanas de la
 * Parte A. El `package.json` del front lleva pins delicados y no entra nada nuevo.
 */

/**
 * Consulta de ejemplo del estado vacio del catalogo.
 *
 * No se traduce: es un termino de busqueda que viaja TAL CUAL al catalogo de
 * OpenRouter, donde los ids son los que son. Traducirlo devolveria cero
 * resultados en el idioma que no casara, que es lo contrario de lo que el
 * estado vacio intenta.
 */
const SUGERENCIA_CATALOGO = "gpt";

/** Precio por millon formateado, o el guion de "OpenRouter no lo declara". */
function precio(valor: number | null, sinDato: string): string {
  if (valor === null || valor === undefined) return sinDato;
  // 4 decimales: los modelos baratos cuestan 0.10 y los nano 0.025 por millon;
  // con 2 decimales varios se leerian como "0.00", es decir, gratis.
  return `$${valor.toFixed(4).replace(/0+$/, "").replace(/\.$/, "")}`;
}

function Precios({ pricing, sinDato }: { pricing: PreciosModelo; sinDato: string }) {
  return (
    <span className="font-mono text-xs">
      {precio(pricing?.input ?? null, sinDato)} / {precio(pricing?.output ?? null, sinDato)}
    </span>
  );
}

/** Chip de capacidad. Es el dato que decide si un modelo se puede asignar. */
function Capacidades({ m }: { m: ModeloDelCatalogo }) {
  const { t } = useTranslation();
  return (
    <span className="flex flex-wrap gap-1">
      {m.supports_tools && (
        <span className="rounded-full bg-[var(--muted)] px-2 py-0.5 text-[11px]">
          {t("admin.modelos.capTools")}
        </span>
      )}
      {m.supports_json && (
        <span className="rounded-full bg-[var(--muted)] px-2 py-0.5 text-[11px]">
          {t("admin.modelos.capJson")}
        </span>
      )}
      {!m.supports_tools && !m.supports_json && (
        <span className="text-[11px] text-[var(--muted-foreground)]">
          {t("admin.modelos.capNinguna")}
        </span>
      )}
    </span>
  );
}

export default function SeccionModelos({ api }: { api: ModelosApi }) {
  const { t, i18n } = useTranslation();

  const [modelos, setModelos] = useState<ModeloCurado[]>([]);
  const [asignaciones, setAsignaciones] = useState<Asignacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);

  // Buscador de alta: consulta al CATALOGO de OpenRouter para dar de alta un
  // modelo. No filtra nada de esta pantalla — ver `filtro`, que es otra cosa.
  const [consulta, setConsulta] = useState("");
  const [resultados, setResultados] = useState<ModeloDelCatalogo[] | null>(null);
  const [buscando, setBuscando] = useState(false);
  // Si el backend corre SIN `OPENROUTER_API_KEY` solo ofrece los `openai/*`.
  // Se guarda para poder explicar una busqueda vacia: sin esto, "gemini" no
  // devuelve nada y el buscador parece roto (reportado en produccion).
  const [modoDirecto, setModoDirecto] = useState(false);

  // Filtro de ESTA pantalla: recorta las asignaciones ya cargadas, en local, sin
  // pedirle nada al backend. Vive en el bloque de asignaciones y no arriba,
  // junto al buscador de alta, porque su sitio es lo que dice sobre que actua.
  const [filtro, setFiltro] = useState("");

  /**
   * Subagentes que el ULTIMO cambio de un agente acaba de arrastrar.
   *
   * Se resaltan un momento tras recargar para que el efecto se VEA: cambiar el
   * modelo de `especificaciones` mueve tres cajas mas, y sin el resalte esas
   * tres cambian de valor en silencio en otra parte del grafo.
   */
  const [arrastrados, setArrastrados] = useState<Set<string>>(new Set());
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Un error de la API se enseña con su `detail` del backend, nunca con un
   * texto generico. Los tres 400 del PUT y el 409 del borrado dicen cosas
   * distintas porque cada una se arregla de otra forma.
   */
  const mostrarError = useCallback(
    (e: unknown) => {
      if (e instanceof ApiError && e.detail) setError(e.detail);
      else setError(t("admin.modelos.errorGenerico"));
    },
    [t],
  );

  const recargar = useCallback(async () => {
    setCargando(true);
    try {
      const [ms, as] = await Promise.all([api.listarModelos(), api.listarAsignaciones()]);
      setModelos(ms);
      setAsignaciones(as);
      setError(null);
    } catch (e) {
      mostrarError(e);
    } finally {
      setCargando(false);
    }
  }, [api, mostrarError]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  const buscarTexto = async (texto: string) => {
    const q = texto.trim();
    if (!q) return;
    setBuscando(true);
    try {
      const { modelos, modoDirecto: directo } = await api.buscarCatalogo(q);
      setResultados(modelos);
      setModoDirecto(directo);
      setError(null);
    } catch (e) {
      mostrarError(e);
    } finally {
      setBuscando(false);
    }
  };

  const buscar = () => buscarTexto(consulta);

  const darDeAlta = async (m: ModeloDelCatalogo) => {
    try {
      await api.altaModelo(m.model_id, m.name ?? null);
      setResultados(null);
      setConsulta("");
      await recargar();
    } catch (e) {
      mostrarError(e);
    }
  };

  const alternarHabilitado = async (m: ModeloCurado) => {
    try {
      await api.editarModelo(m.model_id, { enabled: !m.enabled });
      await recargar();
    } catch (e) {
      mostrarError(e);
    }
  };

  const borrar = async (m: ModeloCurado) => {
    // La confirmacion no sustituye al 409 del backend: es el backend quien sabe
    // si esta en uso, y su mensaje es el que se enseña si lo rechaza.
    if (!window.confirm(t("admin.modelos.confirmarBorrado", { modelo: m.model_id }))) return;
    try {
      await api.borrarModelo(m.model_id);
      await recargar();
    } catch (e) {
      mostrarError(e);
    }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      await api.sincronizarModelos();
      await recargar();
    } catch (e) {
      mostrarError(e);
    } finally {
      setSincronizando(false);
    }
  };

  const asignar = async (a: Asignacion, modelId: string | null) => {
    // Quienes va a arrastrar se calcula ANTES de la llamada: despues de
    // recargar, esos mismos subagentes siguen sin fila propia pero su origen ya
    // es otro, y el conjunto saldria distinto del que de verdad se movio.
    const arrastra =
      a.target_type === "agent"
        ? subagentesArrastrados(asignaciones, a.target_id).map((s) => s.target_id)
        : [];
    try {
      await api.asignarModelo(a.target_type, a.target_id, modelId);
      await recargar();
      // Solo si la llamada fue bien: resaltar tras un 400 afirmaria un efecto
      // que no ocurrio.
      if (arrastra.length > 0) {
        if (temporizador.current) clearTimeout(temporizador.current);
        setArrastrados(new Set(arrastra));
        temporizador.current = setTimeout(() => setArrastrados(new Set()), 4000);
      }
    } catch (e) {
      mostrarError(e);
    }
  };

  // El temporizador del resalte se cancela al desmontar: un `setArrastrados`
  // sobre un componente ya ido es un warning de React y una fuga.
  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    [],
  );

  /**
   * El filtro se aplica UNA vez, aqui, y las dos vistas reciben lo mismo.
   *
   * Filtrar dentro de cada vista daria dos respuestas a "¿que casa?" y la
   * accesible seria la que se queda desactualizada.
   */
  const filtrado = useMemo(() => filtrarAsignaciones(asignaciones, filtro), [asignaciones, filtro]);

  /**
   * El resumen cuenta sobre la lista COMPLETA, no sobre la filtrada: responde
   * "¿que he tocado en este panel?", no "¿de lo que estoy mirando?".
   */
  const resumen = useMemo(() => resumirAsignaciones(asignaciones), [asignaciones]);

  /**
   * Agentes y, tras cada uno, SUS subagentes.
   *
   * En la tabla la jerarquia no se puede dibujar, asi que se representa por
   * ORDEN y sangrado. Listar los cinco subagentes en un bloque aparte los
   * dejaria sin decir de quien cuelga cada uno, que es justo el dato que la
   * vista de grafo aporta y que la alternativa accesible no puede perder.
   */
  const agentes = useMemo(() => {
    const visibles = filtrado.visibles;
    const subagentes = visibles.filter((a) => a.target_type === "subagent");
    const filas: Asignacion[] = [];
    for (const agente of visibles.filter((a) => a.target_type === "agent")) {
      filas.push(agente);
      filas.push(...subagentes.filter((s) => s.parent === agente.target_id));
    }
    // Un subagente cuyo padre no esta en la lista no se descarta: iria a la cola
    // en vez de desaparecer, que dejaria un target invisible e inasignable.
    filas.push(...subagentes.filter((s) => !filas.includes(s)));
    return filas;
  }, [filtrado]);

  const operaciones = useMemo(
    () => filtrado.visibles.filter((a) => a.target_type === "operation"),
    [filtrado],
  );

  const [vista, setVista] = useVistaAsignaciones();

  // Pantalla completa del bloque de asignaciones ENTERO (cabecera, filtro,
  // conmutador y grafo), no solo del canvas: si el boton de salir o el filtro
  // quedaran fuera del elemento a pantalla completa, dentro de ella no se
  // podrian tocar y la unica salida seria Escape — que nadie descubre.
  const bloqueAsignaciones = useRef<HTMLDivElement>(null);
  const pantalla = usePantallaCompleta(bloqueAsignaciones);

  const yaDadoDeAlta = (id: string) => modelos.some((m) => m.model_id === id);

  return (
    <div className="space-y-10">
      {error && (
        <div
          role="alert"
          // Sin `dark:`: en este repo NO hay `@custom-variant dark`, asi que esa
          // variante compila a `prefers-color-scheme` mientras el ThemeProvider
          // usa la clase `.dark` — dos mecanismos distintos, y un usuario con el
          // SO en claro que elija "oscuro" en la app veria la version clara
          // sobre panel oscuro. Un fondo translucido funciona en los dos temas.
          className="flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600"
        >
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* Bloque 1: catalogo curado                                         */}
      {/* ---------------------------------------------------------------- */}
      <div>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium">{t("admin.modelos.catalogoTitulo")}</h2>
            <p className="text-sm text-[var(--muted-foreground)]">
              {t("admin.modelos.catalogoDescripcion")}
            </p>
          </div>
          <button
            onClick={sincronizar}
            disabled={sincronizando}
            className="flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--muted)] disabled:opacity-50"
          >
            <RefreshCw size={14} className={sincronizando ? "animate-spin" : ""} />
            {t("admin.modelos.actualizarPrecios")}
          </button>
        </div>

        {/* Buscador de ALTA. Va SIEMPRE visible, tambien con cero modelos: es
            el unico camino para que la tabla deje de estar vacia.

            Lleva etiqueta VISIBLE, y no solo `aria-label`, porque esta puesto
            donde uno espera un filtro de la pantalla y la confusion era real:
            se teclea el nombre de un agente y no pasa nada visible salvo una
            lista de modelos de OpenRouter. El filtro de verdad esta abajo, en
            el bloque al que afecta. */}
        <div className="mb-4">
          <label
            htmlFor="buscador-catalogo"
            className="mb-1 block text-xs font-medium text-[var(--muted-foreground)]"
          >
            {t("admin.modelos.buscarEtiqueta")}
          </label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]"
              />
              <input
                id="buscador-catalogo"
                value={consulta}
                onChange={(e) => setConsulta(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void buscar();
                }}
                placeholder={t("admin.modelos.buscarPlaceholder")}
                aria-label={t("admin.modelos.buscarAria")}
                className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] py-1.5 pl-9 pr-3 text-sm"
              />
            </div>
            <button
              onClick={buscar}
              disabled={buscando || !consulta.trim()}
              className="rounded-lg bg-[var(--foreground)] px-3 py-1.5 text-sm text-[var(--background)] hover:opacity-90 disabled:opacity-50"
            >
              {t("admin.modelos.buscar")}
            </button>
          </div>
        </div>

        {resultados !== null && (
          <div className="mb-4 overflow-hidden rounded-lg border border-[var(--border)]">
            {resultados.length === 0 ? (
              <p className="px-4 py-3 text-sm text-[var(--muted-foreground)]">
                {/* En modo directo el backend recorta el catalogo a `openai/*`,
                    asi que buscar otro proveedor devuelve vacio SIEMPRE, por
                    bien escrito que este. Decirlo aqui es la diferencia entre
                    "escribe otra cosa" y "configura la clave": el generico
                    mandaba al usuario a reintentar contra algo que no cambia. */}
                {t(modoDirecto ? "admin.modelos.soloOpenai" : "admin.modelos.sinResultados")}
              </p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {resultados.map((m) => (
                    <tr key={m.model_id} className="border-b border-[var(--border)] last:border-0">
                      <td className="px-4 py-2">
                        <div className="font-medium">{m.name || m.model_id}</div>
                        <div className="font-mono text-xs text-[var(--muted-foreground)]">
                          {m.model_id}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Precios pricing={m.pricing} sinDato={t("admin.modelos.sinPrecio")} />
                      </td>
                      <td className="px-4 py-2">
                        <Capacidades m={m} />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => darDeAlta(m)}
                          disabled={yaDadoDeAlta(m.model_id)}
                          className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--muted)] disabled:opacity-50"
                        >
                          <Plus size={12} />
                          {yaDadoDeAlta(m.model_id)
                            ? t("admin.modelos.yaDadoDeAlta")
                            : t("admin.modelos.darDeAlta")}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {cargando ? (
          <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
            {t("admin.modelos.cargando")}
          </p>
        ) : modelos.length === 0 ? (
          // Estado vacio REAL: hoy `llm_models` esta vacia, asi que este es el
          // primer pantallazo del panel. Una tabla en blanco no diria que hacer.
          <div className="rounded-lg border border-dashed border-[var(--border)] px-4 py-8 text-center">
            <p className="text-sm font-medium">{t("admin.modelos.vacioTitulo")}</p>
            <p className="mx-auto mt-1 max-w-lg text-sm text-[var(--muted-foreground)]">
              {t("admin.modelos.vacioAyuda")}
            </p>
            {/* El camino, no solo el diagnostico: sin modelos dados de alta, las
                asignaciones de abajo no sirven de nada, asi que el estado vacio
                tiene que llevar al unico sitio donde se arregla. El boton pone
                una consulta de ejemplo Y la lanza: el usuario ve resultados
                reales en vez de tener que adivinar que se teclea ahi. */}
            <button
              type="button"
              onClick={() => {
                setConsulta(SUGERENCIA_CATALOGO);
                void buscarTexto(SUGERENCIA_CATALOGO);
                document.getElementById("buscador-catalogo")?.focus();
              }}
              className="mt-3 inline-flex items-center gap-1 rounded-lg border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--muted)]"
            >
              <Search size={14} />
              {t("admin.modelos.vacioAccion", { ejemplo: SUGERENCIA_CATALOGO })}
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-[var(--border)]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
                  <th className="px-4 py-2 text-left font-medium">
                    {t("admin.modelos.colModelo")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("admin.modelos.colPrecios")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("admin.modelos.colContexto")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("admin.modelos.colCapacidades")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    {t("admin.modelos.colSincronizado")}
                  </th>
                  <th className="px-4 py-2 text-left font-medium">{t("admin.active")}</th>
                  <th className="w-12 px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {modelos.map((m) => {
                  const estado = estadoSincronizacion(m.synced_at);
                  return (
                    <tr key={m.model_id} className="border-b border-[var(--border)] last:border-0">
                      <td className="px-4 py-2">
                        <div className="font-medium">{m.label || m.name || m.model_id}</div>
                        <div className="font-mono text-xs text-[var(--muted-foreground)]">
                          {m.model_id}
                        </div>
                      </td>
                      <td className="px-4 py-2">
                        <Precios pricing={m.pricing} sinDato={t("admin.modelos.sinPrecio")} />
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {m.context_length
                          ? m.context_length.toLocaleString(i18n.language)
                          : t("admin.modelos.sinPrecio")}
                      </td>
                      <td className="px-4 py-2">
                        <Capacidades m={m} />
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {/* "Nunca sincronizado" y "precios viejos" son avisos
                            DISTINTOS: el primero es que no hay precio fiable de
                            ninguna edad, el segundo que la sincronizacion dejo
                            de correr. Ver `estadoSincronizacion`. */}
                        {estado === "nunca" ? (
                          <span className="inline-flex items-center gap-1 text-amber-600">
                            <AlertTriangle size={12} />
                            {t("admin.modelos.nuncaSincronizado")}
                          </span>
                        ) : estado === "viejo" ? (
                          <span
                            className="inline-flex items-center gap-1 text-amber-600"
                            title={t("admin.modelos.preciosViejosAyuda")}
                          >
                            <AlertTriangle size={12} />
                            {t("admin.modelos.preciosViejos", {
                              fecha: new Date(m.synced_at as string).toLocaleDateString(
                                i18n.language,
                              ),
                            })}
                          </span>
                        ) : (
                          <span className="text-[var(--muted-foreground)]">
                            {new Date(m.synced_at as string).toLocaleDateString(i18n.language)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => alternarHabilitado(m)}
                          className={`rounded-full px-2 py-0.5 text-xs ${
                            m.enabled
                              ? "bg-green-500/15 text-green-600"
                              : "bg-red-500/15 text-red-600"
                          }`}
                        >
                          {m.enabled ? t("admin.active") : t("admin.inactive")}
                        </button>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => borrar(m)}
                          title={t("admin.modelos.borrar")}
                          aria-label={t("admin.modelos.borrarAria", { modelo: m.model_id })}
                          className="rounded p-1 hover:bg-[var(--muted)]"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Bloque 2: asignaciones                                            */}
      {/* ---------------------------------------------------------------- */}
      <div
        ref={bloqueAsignaciones}
        // A pantalla completa el navegador pone el elemento a tamaño de ventana
        // sobre un fondo negro: hace falta darle fondo, margen y una columna
        // flex para que el grafo (`flex-1`) tome el alto que quede tras la
        // cabecera y el filtro. En pagina normal no cambia nada.
        // `data-pantalla-completa` es el observable de los tests.
        data-pantalla-completa={pantalla.activa ? "si" : undefined}
        className={
          pantalla.activa
            ? "flex h-full flex-col overflow-auto bg-[var(--background)] p-4"
            : undefined
        }
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium">{t("admin.modelos.asignacionesTitulo")}</h2>
            <p className="text-sm text-[var(--muted-foreground)]">
              {t("admin.modelos.asignacionesDescripcion")}
            </p>
            {/* Resumen: responde de un vistazo "¿que he tocado yo aqui?" sin
                tener que recorrer 28 cajas contando chips. Los tres origenes
                por separado y no dos: un subagente heredado corre con un modelo
                que alguien ELIGIO (el de su padre), y meterlo en el saco del
                default diria que nadie toco nada donde si se toco. */}
            {resumen.total > 0 && (
              <p className="mt-1 text-sm">
                <span className="font-medium">
                  {t("admin.modelos.resumenAsignados", { count: resumen.asignados })}
                </span>
                <span className="text-[var(--muted-foreground)]">
                  {" · "}
                  {t("admin.modelos.resumenDefault", { count: resumen.porDefecto })}
                  {resumen.heredados > 0 && (
                    <>
                      {" · "}
                      {t("admin.modelos.resumenHeredados", { count: resumen.heredados })}
                    </>
                  )}
                </span>
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
          {/* Conmutador de vista. `radiogroup` y no dos botones sueltos: son
              dos alternativas EXCLUYENTES de la misma cosa, y asi un lector de
              pantalla anuncia cual esta activa y las flechas del teclado
              recorren las dos. */}
          <div
            role="radiogroup"
            aria-label={t("admin.modelos.vistaAria")}
            className="flex shrink-0 rounded-lg border border-[var(--border)] p-0.5"
          >
            {VISTAS.map((v) => (
              <button
                key={v}
                type="button"
                role="radio"
                aria-checked={vista === v}
                onClick={() => setVista(v)}
                className={`rounded-md px-3 py-1 text-sm ${
                  vista === v
                    ? "bg-[var(--muted)] font-medium"
                    : "text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                }`}
              >
                {v === "grafo" ? t("admin.modelos.vistaGrafo") : t("admin.modelos.vistaTabla")}
              </button>
            ))}
          </div>

          {/* Pantalla completa. Solo si el navegador la ofrece (en un iframe
              sin permiso o sin API el boton no se pinta: uno que no hace nada
              es peor que ninguno) y solo con el grafo a la vista — o ya DENTRO
              de la pantalla completa, para que conmutar a tabla ahi no se lleve
              el boton de salir. El icono y el texto los fija el evento del
              navegador, no el clic: Escape sale sin pasar por aqui. */}
          {pantalla.disponible && (vista === "grafo" || pantalla.activa) && (
            <button
              type="button"
              onClick={pantalla.alternar}
              aria-pressed={pantalla.activa}
              aria-label={
                pantalla.activa
                  ? t("admin.modelos.salirPantallaCompleta")
                  : t("admin.modelos.pantallaCompleta")
              }
              title={
                pantalla.activa
                  ? t("admin.modelos.salirPantallaCompleta")
                  : t("admin.modelos.pantallaCompleta")
              }
              className="rounded-lg border border-[var(--border)] p-1.5 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
            >
              {pantalla.activa ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          )}
          </div>
        </div>

        {/* Filtro de la pantalla. Vive AQUI, pegado a lo que recorta, y no
            arriba junto al buscador de alta: dos cajas de texto seguidas que
            hacen cosas distintas son exactamente la confusion que este cambio
            viene a deshacer. Filtra en local, sin pedir nada al backend. */}
        <div className="mb-4">
          <label
            htmlFor="filtro-asignaciones"
            className="mb-1 block text-xs font-medium text-[var(--muted-foreground)]"
          >
            {t("admin.modelos.filtroEtiqueta")}
          </label>
          <div className="relative max-w-md">
            <Filter
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-foreground)]"
            />
            <input
              id="filtro-asignaciones"
              type="search"
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder={t("admin.modelos.filtroPlaceholder")}
              aria-label={t("admin.modelos.filtroAria")}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] py-1.5 pl-9 pr-3 text-sm"
            />
          </div>
          {/* Cuenta de resultados, en vivo para un lector de pantalla: sin ella,
              filtrar en la vista de grafo es un canvas que cambia sin decir por
              que, y "no hay nada" es indistinguible de "no ha filtrado". */}
          {filtro.trim() !== "" && (
            <p className="mt-1 text-xs text-[var(--muted-foreground)]" role="status">
              {filtrado.numCasan === 0
                ? t("admin.modelos.filtroSinResultados", { consulta: filtro.trim() })
                : t("admin.modelos.filtroResultados", { count: filtrado.numCasan })}
            </p>
          )}
        </div>

        {vista === "grafo" ? (
          <GrafoModelos
            asignaciones={filtrado.visibles}
            modelos={modelos}
            onAsignar={asignar}
            papel={filtrado.papel}
            resaltados={arrastrados}
            pantallaCompleta={pantalla.activa}
          />
        ) : (
          <>
            <TablaAsignaciones
              titulo={t("admin.modelos.agentes")}
              filas={agentes}
              modelos={modelos}
              onAsignar={asignar}
              papel={filtrado.papel}
              resaltados={arrastrados}
            />
            <div className="mt-6">
              <TablaAsignaciones
                titulo={t("admin.modelos.tareas")}
                filas={operaciones}
                modelos={modelos}
                onAsignar={asignar}
                papel={filtrado.papel}
                resaltados={arrastrados}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TablaAsignaciones({
  titulo,
  filas,
  modelos,
  onAsignar,
  papel,
  resaltados,
}: {
  titulo: string;
  /** Ya FILTRADAS por el padre: la tabla pinta lo que recibe. */
  filas: Asignacion[];
  modelos: ModeloCurado[];
  onAsignar: (a: Asignacion, modelId: string | null) => void;
  papel?: Map<string, Coincidencia>;
  resaltados?: Set<string>;
}) {
  const { t } = useTranslation();

  /** Cuantos subagentes VISIBLES arrastraria cada agente de esta tabla. */
  const arrastres = useMemo(() => contarArrastres(filas), [filas]);

  // Una tabla sin filas tras filtrar no se pinta vacia con su cabecera: eso
  // parece un fallo de carga. El mensaje de "no hay resultados" lo da el padre
  // una sola vez, y aqui simplemente no se ocupa sitio.
  if (filas.length === 0) return null;

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-[var(--muted-foreground)]">{titulo}</h3>
      {/* `overflow-x-auto` y NO `overflow-hidden`: el desplegable de modelo
          crecio al añadirle el precio, y con `hidden` la columna "Asignar" se
          RECORTABA por la derecha sin forma de llegar a ella. Recortar un
          control con el que hay que interactuar no es una degradacion
          aceptable; desplazar, si. El `rounded-lg` sigue aqui para que las
          esquinas recorten la tabla. */}
      <div className="overflow-x-auto rounded-lg border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--muted)]">
              <th className="px-4 py-2 text-left font-medium">{t("admin.modelos.colTarget")}</th>
              {/* La tabla es la ALTERNATIVA ACCESIBLE del grafo, asi que lleva
                  las mismas insignias y por el mismo componente: si aqui se
                  compusieran aparte, la vista accesible acabaria contando menos
                  que la otra, que es justo lo que no puede hacer. */}
              <th className="px-4 py-2 text-left font-medium">
                {t("admin.modelos.colCaracteristicas")}
              </th>
              <th className="px-4 py-2 text-left font-medium">{t("admin.modelos.colEfectivo")}</th>
              <th className="px-4 py-2 text-left font-medium">{t("admin.modelos.colOrigen")}</th>
              <th className="px-4 py-2 text-left font-medium">{t("admin.modelos.colAsignar")}</th>
            </tr>
          </thead>
          <tbody>
            {filas.map((a) => {
              // Filtrado POR CAPACIDAD y por tipo de target: un modelo sin
              // `tools` rompe un agente ReAct (el backend lo rechaza con 400),
              // pero vale de sobra para una operacion auxiliar, que no usa tools.
              const disponibles = modelosParaTarget(modelos, a.target_type);
              const origen = origenConocido(a.origen);
              const esSubagente = a.target_type === "subagent";
              const esContexto = papel?.get(a.target_id) === "contexto";
              const arrastrado = resaltados?.has(a.target_id) ?? false;
              const arrastra = arrastres.get(a.target_id) ?? 0;
              return (
                <tr
                  key={a.target_id}
                  // `contexto`: el filtro no lo encontro y solo esta aqui para
                  // que su subagente no quede sin padre encima. `arrastrado`:
                  // su modelo acaba de moverse porque cambio el de su agente.
                  data-papel={esContexto ? "contexto" : "casa"}
                  data-arrastrado={arrastrado ? "si" : undefined}
                  className={`border-b border-[var(--border)] last:border-0 ${
                    esContexto ? "opacity-40" : ""
                  } ${arrastrado ? "bg-amber-500/10" : ""}`}
                >
                  {/* Un subagente va sangrado bajo su agente: en una tabla la
                      relacion padre-hijo no se puede dibujar, asi que la dicen
                      el orden y la sangria. */}
                  <td className={`px-4 py-2 font-medium ${esSubagente ? "pl-10" : ""}`}>
                    {esSubagente && (
                      <span aria-hidden className="mr-1 text-[var(--muted-foreground)]">
                        &#8627;
                      </span>
                    )}
                    <span title={a.target_id}>{a.label || a.target_id}</span>
                    {/* El padre, para lectores de pantalla y para quien lea la
                        tabla fuera de contexto: la sangria es visual y no se
                        anuncia. */}
                    {esSubagente && a.parent && (
                      <span className="sr-only">
                        {t("admin.modelos.subagenteDe", { padre: a.parent })}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <InsigniasAgente
                      caracteristicas={a.caracteristicas}
                      esSubagente={esSubagente}
                    />
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{a.model_id}</td>
                  <td className="px-4 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] ${
                        origen === "asignado"
                          ? "bg-blue-500/15 text-blue-600"
                          : origen === "heredado"
                            ? "bg-amber-500/15 text-amber-600"
                            : "bg-[var(--muted)] text-[var(--muted-foreground)]"
                      }`}
                    >
                      {origen === "asignado"
                        ? t("admin.modelos.origenAsignado")
                        : origen === "heredado"
                          ? t("admin.modelos.origenHeredado")
                          : t("admin.modelos.origenDefault")}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <select
                        // `value` es el modelo asignado, o "" cuando el target
                        // corre con el default: el modelo efectivo de un target
                        // en default puede no estar ni dado de alta, y ponerlo
                        // como `value` haria que el desplegable mostrara un
                        // valor que no tiene opcion (y el navegador elegiria la
                        // primera, afirmando una asignacion que no existe).
                        value={origen === "asignado" ? a.model_id : ""}
                        aria-label={t("admin.modelos.asignarAria", { target: a.label })}
                        onChange={(e) => onAsignar(a, e.target.value || null)}
                        // `max-w-full` para que el desplegable no ensanche la
                        // tabla por si solo: su contenido mas largo es una
                        // opcion con nombre de modelo y precio, y sin tope la
                        // columna crece hasta forzar scroll horizontal en una
                        // tabla que cabria. El texto que no entre se recorta en
                        // el control cerrado, no en la pagina, y al desplegarlo
                        // se ve entero.
                        className="max-w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-2 py-1 text-xs"
                      >
                        {/* Para un subagente, quitar la asignacion no lo devuelve
                            al default del codigo: lo devuelve al modelo de su
                            AGENTE, que puede ser otro. Son cosas distintas y el
                            desplegable tiene que decirlo. */}
                        <option value="">
                          {esSubagente
                            ? t("admin.modelos.usarDelAgente")
                            : t("admin.modelos.usarDefault")}
                        </option>
                        {disponibles.map((m) => (
                          // Nombre Y precio por millon: es la decision cara de
                          // esta pantalla y el dato ya viaja en la lista.
                          <option key={m.model_id} value={m.model_id}>
                            {etiquetaModelo(m)}
                          </option>
                        ))}
                      </select>
                      {origen === "asignado" && (
                        <button
                          onClick={() => onAsignar(a, null)}
                          title={
                            esSubagente
                              ? t("admin.modelos.volverAlAgente")
                              : t("admin.modelos.volverDefault")
                          }
                          aria-label={
                            esSubagente
                              ? t("admin.modelos.volverAlAgenteAria", { target: a.label })
                              : t("admin.modelos.volverDefaultAria", { target: a.label })
                          }
                          className="rounded p-1 hover:bg-[var(--muted)]"
                        >
                          <RotateCcw size={14} />
                        </button>
                      )}
                    </div>
                    {/* El alcance real del cambio, en la celda donde se hace.
                        La precedencia es "gana lo mas especifico": el agente
                        arrastra a los subagentes SIN asignacion propia y deja
                        quieto al que tiene la suya. Sin esto, quien cambia
                        `especificaciones` cree que toca un target y toca
                        cuatro. Es un aviso, no un modal que estorbe. */}
                    {arrastra > 0 && (
                      <p className="mt-1 text-[11px] text-amber-600">
                        {t("admin.modelos.arrastraSubagentes", { count: arrastra })}
                      </p>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
